import React from "react";
import { motion } from "framer-motion";
import BackgroundImage from "../../../../components/ui/BackgroundImage";
import greenImg from "../../assets/images/green.png";
import purpleImg from "../../assets/images/purple.png";

export type ButtonVariant = "green" | "purple" | "active" | "neutral";

const buttonVariantImage: Record<ButtonVariant, string> = {
  green: greenImg,
  purple: purpleImg,
  active: "/assets/buttons/button-active.png",
  neutral: "/assets/buttons/button-neutral.png",
};

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  className?: string;
  children: React.ReactNode;
};

// ─── Variants ────────────────────────────────────────────────────────────────

const wrapperVariants: any = {
  mount: { opacity: 0, scale: 0.85 },
  idle: {
    opacity: 1,
    scale: 1,
    filter: "brightness(1)",
    transition: { type: "spring", stiffness: 350, damping: 22 },
  },
  hover: {
    scale: 1.06,
    filter: "brightness(1.12)",
    transition: { type: "spring", stiffness: 400, damping: 18 },
  },
  tap: {
    scale: 0.88,
    filter: "brightness(0.82)",
    transition: { type: "spring", stiffness: 600, damping: 20 },
  },
};

const shimmerVariants: any = {
  mount: { x: "-100%", opacity: 0 },
  idle: { x: "-100%", opacity: 0 },
  hover: {
    x: "280%",
    opacity: [0, 0.55, 0],
    transition: { duration: 0.55, ease: "easeInOut" },
  },
  tap: { x: "-100%", opacity: 0 },
};

const labelVariants: any = {
  mount: {},
  idle: { y: 0, letterSpacing: "0em", scale: 1 },
  hover: {
    letterSpacing: "0.04em",
    scale: 1,
    transition: { type: "spring", stiffness: 500, damping: 20 },
  },
  tap: {
    letterSpacing: "0em",
    scale: 0.95,
    transition: { type: "spring", stiffness: 600, damping: 18 },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const Button = ({
  variant = "neutral",
  onClick,
  disabled = false,
  className,
  children,
}: ButtonProps) => {
  return (
    <motion.div
      className="rounded-md"
      variants={wrapperVariants}
      initial="mount"
      animate="idle"
      whileHover={!disabled ? "hover" : undefined}
      whileTap={!disabled ? "tap" : undefined}
      style={{
        display: "inline-block",
        position: "relative",
        overflow: "hidden",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {/* Shimmer sweep overlay */}
      <motion.div
        variants={shimmerVariants}
        style={{
          position: "absolute",
          inset: 0,
          width: "35%",
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)",
          zIndex: 10,
          pointerEvents: "none",
        }}
      />

      <BackgroundImage
        imageSrc={buttonVariantImage[variant]}
        imageClassName="!object-fill"
      >
        <button
          onClick={onClick}
          disabled={disabled}
          className={`text-[1rem] ${className}`}
        >
          <motion.span
            className="flex items-center justify-center w-full h-full"
            variants={labelVariants}
          >
            {children}
          </motion.span>
        </button>
      </BackgroundImage>
    </motion.div>
  );
};

export default Button;
